import { environmentHttpClient } from "client/Api/httpClient";
import {
	AppColorViewModel,
	AppType,
	AppCategory,
	CalendarDestinationApp,
	ClientlessRdpDestinationWindow,
	DestinationWindow,
	EmailDestinationApp
} from "client/Api/Apps/common";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { DocumentItemViewModel, SharePointColumnId } from "client/Api/Documents/SharePoint/common";
import { ExchangeFolderId } from "client/Api/Email/common";
import {
	EmailDateFilterKind,
	EmailFilterSelector,
	EmailListItemModel,
	EmailSortDirection,
	EmailSortOrder
} from "client/Api/Email/email";
import { ContactListItem, ContactSource } from "client/Api/Email/contacts";
import { SortDirection } from "client/Api/common";
import {
	AnnouncementPagingInformationModel,
	AnnouncementPreviewItemModel,
	AnnouncementSortingInformationModel
} from "client/Api/Hub/Announcements/announcementViewer";
import {
	EventDateFilterType,
	EventDateSortDirection,
	EventLocationType,
	EventPagingInformationModel,
	EventPreviewModel
} from "client/Api/Hub/Events/eventViewer";
import {
	KnowledgeItemOrderModel,
	KnowledgeItemPagingInformationModel,
	KnowledgeItemPreviewItemModel
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { SearchNodeKind } from "client/Api/SearchSettings/searchSettings";
import { CommunityPostImageViewModel } from "client/Api/Communities/communityPostViewer";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { CommunityViewModel } from "client/Api/Communities/communityPosts";
import { CommunityVisibility } from "client/Api/Communities/community";
import { TagViewModel } from "client/Api/Tags/tags";
import { ReactionCounterModel } from "client/Api/Hub/common";
import { RemoteDesktopProtocolClientKind } from "client/Api/Apps/EditApp/saveApp";

export const enum SearchIntegrationKind {
	Apps = 1,
	Files = 2,
	Emails = 3,
	Contacts = 4,
	Folders = 5,
	Announcements = 6,
	KnowledgeItems = 7,
	Egnyte = 9,
	Events = 10,
	Communities = 11,
	CommunityPosts = 12
}

export type SearchRequest = {
	maxItems: number;
	predefinedIntegrationKind?: SearchIntegrationKind;
	customIntegrationId?: number;
	searchTerm: string;
};

export enum GlobalSearchResultItemKind {
	App = 1,
	Document = 2,
	Email = 3,
	Contact = 4,
	Announcement = 5,
	KnowledgeItems = 6,
	EgnyteItem = 8,
	Event = 9,
	IntegrationFlow = 10,
	Community = 11,
	CommunityPost = 12
}

export type HighlightItem = {
	startPosition: number;
	fragmentLength: number;
	highlightedField: string;
};

type AppNavigationParametersWithDestinationWindow = {
	destinationWindow: DestinationWindow;
};

type EmailAppNavigationParameters = AppNavigationParametersWithDestinationWindow & {
	destinationApp: EmailDestinationApp;
};

type CalendarAppNavigationParameters = AppNavigationParametersWithDestinationWindow & {
	destinationApp: CalendarDestinationApp;
};

type ClientlessRdpAppNavigationParameters = {
	destinationWindow: ClientlessRdpDestinationWindow;
};

type WindowsVirtualDesktopNavigationParameters = {
	clientKind: RemoteDesktopProtocolClientKind;
};

type AppWithDestinationWindowNavigationParameters = {
	appType:
		| AppType.Autotask
		| AppType.AzureApp
		| AppType.Documents
		| AppType.TopDesk
		| AppType.Url
		| AppType.VilansKick
		| AppType.ConnectWise
		| AppType.Google
		| AppType.SharePoint
		| AppType.Teams
		| AppType.ToDo
		| AppType.Liquit
		| AppType.Magister
		| AppType.NedapOns
		| AppType.Afas
		| AppType.VmwareHorizonApp
		| AppType.Zaaksysteem
		| AppType.Zermelo
		| AppType.ZenyaDoc;
	parameters: AppNavigationParametersWithDestinationWindow;
};

export type AppWithNavigationParameters = AppWithDestinationWindowNavigationParameters
	| {
		appType: AppType.Email;
		parameters: EmailAppNavigationParameters;
	}
	| {
		appType: AppType.ClientlessRdp;
		parameters: ClientlessRdpAppNavigationParameters;
	}
	| {
		appType: AppType.Office365Calendar;
		parameters: CalendarAppNavigationParameters;
	}
	| {
		appType: AppType.WindowsVirtualDesktop;
		parameters: WindowsVirtualDesktopNavigationParameters;
	};

type ExcludedAppTypes =
	| AppType.Unknown
	| AppType.Birthday
	| AppType.HowTo
	| AppType.PowerBi
	| AppType.Rss
	| AppType.YouTube
	| AppType.Integration;

type IncludedAppTypes = Exclude<AppType, ExcludedAppTypes>;

export type AppWithoutParameters = {
	appType: Exclude<IncludedAppTypes, AppWithNavigationParameters["appType"]>;
};

export type MaintenanceWindowAppModel = {
	endsOn: Date;
	notificationMessage: string;
};

export type AppGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.App;
	hitHighlights: readonly HighlightItem[];
	id: number;
	name: string;
	color: AppColorViewModel;
	image: AppImageDisplayInfo;
	category: AppCategory;
	maintenanceWindow: MaintenanceWindowAppModel | null;
	tags: readonly TagViewModel[];
	tagsHitHighlights: readonly (readonly HighlightItem[])[];
} &
(
	AppWithNavigationParameters
	|
	AppWithoutParameters
);

export type DocumentGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.Document;
	hitHighlights: readonly HighlightItem[];
	document: DocumentItemViewModel;
};

export type EmailGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.Email;
	item: EmailListItemModel;
	hitHighlights: readonly HighlightItem[];
	parentFolderName: string | null;
	color: AppColorViewModel;
};

export type ContactGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.Contact;
	hitHighlights: readonly HighlightItem[];
	contact: ContactListItem;
	contactSource: ContactSource;
};

export type AnnouncementGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.Announcement;
	hitHighlights: readonly HighlightItem[];
	announcement: AnnouncementPreviewItemModel;
};

export type KnowledgeItemGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.KnowledgeItems;
	hitHighlights: readonly HighlightItem[];
	knowledgeItem: KnowledgeItemPreviewItemModel;
};

export type EventGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.Event;
	hitHighlights: readonly HighlightItem[];
	event: EventPreviewModel;
};

export type EgnyteGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.EgnyteItem;
	hitHighlights: readonly HighlightItem[];
	name: string;
	extension: string;
	snippet: string;
	path: string;
	url: string;
	locationUrl: string;
	lastModifiedDate: Date | string;
	modifiedBy: string;
};

export type IntegrationFlowGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.IntegrationFlow;
	hitHighlights: readonly HighlightItem[];
	title: string | null;
	description: string | null;
	subTitle1: string | null;
	subTitle2: string | null;
	link: string | null;
	dateTime: string | Date | null;
	color: AppColorViewModel;
	icon: AppImageDisplayInfo;
};

export type IGlobalSearchResultItem =
	| AppGlobalSearchResultItem
	| DocumentGlobalSearchResultItem
	| EmailGlobalSearchResultItem
	| ContactGlobalSearchResultItem
	| AnnouncementGlobalSearchResultItem
	| KnowledgeItemGlobalSearchResultItem
	| EgnyteGlobalSearchResultItem
	| EventGlobalSearchResultItem
	| IntegrationFlowGlobalSearchResultItem
	| CommunityGlobalSearchResultItem
	| CommunityPostGlobalSearchResultItem;

export const enum GlobalSearchCategoryResultStatus {
	Success = 1,
	Failed = 2,
	AuthenticationRequired = 3
}

export enum GlobalSearchScopeKind {
	Predefined = 1,
	IntegrationFlow = 2
}

export type GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined;
	predefinedKind: SearchIntegrationKind;
} | {
	kind: GlobalSearchScopeKind.IntegrationFlow;
	integrationId: number;
};

export type GlobalSearchCategoryResult = {
	name: string;
	itemsScope: GlobalSearchScopeIdentifier;
} & (
		{
			status: GlobalSearchCategoryResultStatus.Success;
			items: readonly IGlobalSearchResultItem[];
		}
		|
		{
			status: Exclude<GlobalSearchCategoryResultStatus, GlobalSearchCategoryResultStatus.Success>;
			items: null;
		}
	);

export type GlobalSearchTermModel = {
	id: number;
	searchTerm: string;
	lastUsed: Date | string;
};

type GlobalSearchRegisterSearchTermModel = {
	searchTerm: string;
};

export function search(
	model: SearchRequest,
	signal?: AbortSignal
): Promise<GlobalSearchCategoryResult | null> {
	return environmentHttpClient.get<GlobalSearchCategoryResult | null>(
		"/TitleBar/Search",
		model,
		{
			signal
		}
	);
}

export enum EgnyteSortProperty {
	Name = 1,
	ModifiedDate = 2
};

export type EgnyteOrderModel = {
	property: EgnyteSortProperty;
	direction: SortDirection;
};

export type EgnyteSearchRequestModel = {
	searchTerm: string;
	timeScope: SharePointSearchTimeScope | null;
	skip: number | null;
	take: number | null;
	order: EgnyteOrderModel | null;
};

export function searchEgnyte(
	request: EgnyteSearchRequestModel,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<EgnyteGlobalSearchResultItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<EgnyteGlobalSearchResultItem>>(
		"/TitleBar/Search/Egnyte",
		request,
		{ signal }
	);
}

export async function registerSearchTerm(
	searchTerm: string,
	signal?: AbortSignal
): Promise<GlobalSearchTermModel> {
	const model: GlobalSearchRegisterSearchTermModel = {
		searchTerm: searchTerm
	};

	return environmentHttpClient.post<GlobalSearchTermModel>(
		`TitleBar/Search/SearchTerm`,
		model,
		{ signal }
	);
}

export async function listSearchTerms(
	signal?: AbortSignal
): Promise<GlobalSearchTermModel[]> {
	return environmentHttpClient.get<GlobalSearchTermModel[]>(
		"TitleBar/Search/SearchTerms",
		undefined,
		{ signal }
	);
}

export async function deleteSearchTerms(
	ids: readonly number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		"TitleBar/Search/SearchTerms",
		ids,
		{ signal }
	);
}

export async function registerGlobalSearchItemInteraction(
	scope: GlobalSearchScopeIdentifier
): Promise<void> {
	return environmentHttpClient.post<void>(
		"TitleBar/Search/Usage",
		scope
	);
}

export enum SharePointSearchTimeScope {
	AnyTime = 1,
	Last24Hrs = 2,
	LastWeek = 3,
	LastMonth = 4,
	Last3Months = 5,
	LastYear = 6,
	OlderThenAYear = 7
};

export enum GlobalFileSearchDocumentKind {
	Word = 1,
	Excel = 2,
	PowerPoint = 3,
	OneNote = 4,
	Visio = 5,
	PDF = 6,
	Photo = 7,
	Video = 8,
	WebPage = 9,
	Other = 10
}

export enum SharePointSearchValue {
	All = 0,
	Title = 1
}

export type SharePointOrderModel = {
	property: SharePointColumnId;
	direction: SortDirection;
};

export type GlobalFileSearchRequestModel = {
	searchTerm: string;
	documentKinds: readonly GlobalFileSearchDocumentKind[];
	timeScope: SharePointSearchTimeScope;
	searchIn: SharePointSearchValue;
	skip: number;
	take: number;
	order: SharePointOrderModel | null;
};

export type GlobalFileSearchResultModel = {
	kind: GlobalSearchResultItemKind.Document;
	document: DocumentItemViewModel;
	content: string;
	hitHighlights: readonly HighlightItem[];
};

export function searchFiles(
	request: GlobalFileSearchRequestModel,
	signal?: AbortSignal
): Promise<PageResult<GlobalFileSearchResultModel>> {
	return environmentHttpClient.get<PageResult<GlobalFileSearchResultModel>>(
		"/TitleBar/Search/Files",
		request,
		{
			signal
		}
	);
}

export type FolderSearchRequestModel = {
	searchTerm: string;
	timeScope: SharePointSearchTimeScope;
	skip: number;
	take: number;
	order: SharePointOrderModel | null;
};

export function searchFolders(
	request: FolderSearchRequestModel,
	signal?: AbortSignal
): Promise<PageResult<DocumentGlobalSearchResultItem>> {
	return environmentHttpClient.get<PageResult<DocumentGlobalSearchResultItem>>(
		"/TitleBar/Search/Folders",
		request,
		{
			signal
		}
	);
}

export type AnnouncementSearchRequestModel = {
	searchTerm: string;
	categoryIds: readonly number[];
	authorIds: readonly number[];
	sortOrder: AnnouncementSortingInformationModel;
	afterAnnouncement: OptionalModel<AnnouncementPagingInformationModel>;
	amount: number;
};

export function searchAnnouncements(
	request: AnnouncementSearchRequestModel,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<AnnouncementGlobalSearchResultItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<AnnouncementGlobalSearchResultItem>>(
		"/TitleBar/Search/Announcements",
		request,
		{
			signal
		}
	);
}

export type ExtendedEmailSearchRequestModel = {
	take: number;
	skip: number;
	filterFolderId: OptionalModel<ExchangeFolderId>;
	sortOrder: EmailSortOrder;
	sortDirection: EmailSortDirection;
	searchTerm: string;
	filterSelectors: readonly EmailFilterSelector[];
	dateFilterKind: EmailDateFilterKind;
};

export type ExtendedEmailSearchResultsModel = {
	results: LazyLoadPageResult<EmailGlobalSearchResultItem>;
};

export function searchEmail(
	model: ExtendedEmailSearchRequestModel,
	signal?: AbortSignal
): Promise<ExtendedEmailSearchResultsModel> {
	return environmentHttpClient.get<ExtendedEmailSearchResultsModel>(
		"TitleBar/Search/Email",
		model,
		{ signal }
	);
}

export type EventSearchRequestModel = {
	searchTerm: string;
	dateFilterType: EventDateFilterType;
	categoryIds: readonly number[];
	locationTypes: readonly EventLocationType[];
	sortDateDirection: EventDateSortDirection;
	pagingInformation: OptionalModel<EventPagingInformationModel>;
	amount: number;
};

export function searchEvents(
	request: EventSearchRequestModel,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<EventGlobalSearchResultItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<EventGlobalSearchResultItem>>(
		"/TitleBar/Search/Events",
		request,
		{
			signal
		}
	);
}

export type KnowledgeItemSearchRequestModel = {
	searchTerm: string;
	categoryIds: readonly number[];
	authorIds: readonly number[];
	sortOrder: KnowledgeItemOrderModel;
	amount: number;
	pagingInformation: OptionalModel<KnowledgeItemPagingInformationModel>;
};

export function searchKnowledgeItems(
	request: KnowledgeItemSearchRequestModel,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<KnowledgeItemGlobalSearchResultItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<KnowledgeItemGlobalSearchResultItem>>(
		"/TitleBar/Search/KnowledgeItems",
		request,
		{
			signal
		}
	);
}

export function getIsFullTextSearchEnabled(signal?: AbortSignal): Promise<boolean> {
	return environmentHttpClient.get<boolean>(
		"/TitleBar/Search/KnowledgeItems/IsFullTextSearchEnabled",
		undefined,
		{ signal }
	);
}

export type AppSearchRequestModel = {
	searchTerm: string;
	categories: readonly AppCategory[];
	sortDirection: SortDirection;
	take?: number;
};

export function searchApps(
	request: AppSearchRequestModel,
	signal?: AbortSignal
): Promise<readonly AppGlobalSearchResultItem[]> {
	return environmentHttpClient.get<readonly AppGlobalSearchResultItem[]>(
		"/TitleBar/Search/Apps",
		request,
		{
			signal
		}
	);
}

export const enum UserSortBy {
	Default = 0,
	FirstName = 1,
	LastName = 2,
	JobTitle = 3,
	Department = 4,
	OfficeLocation = 5
}

export const enum ContactSearchSource {
	All = 0,
	Workspace = 1,
	Personal = 2,
	Organisation = 3
}

export type DefaultContactExtendedSearchRequestModel = {
	source: Exclude<ContactSearchSource, ContactSearchSource.Organisation>;
	searchTerm: string;
	sortBy?: UserSortBy;
	jobTitles: readonly string[];
	departments: readonly string[];
	officeLocations: readonly string[];
	take: number;
	skip: number;
};

export type GalContactExtendedSearchRequestModel = {
	source: ContactSearchSource.Organisation;
	searchTerm: string;
	take: number;
	skipToken: string | null;
};

export type ContactExtendedSearchRequestModel =
	| DefaultContactExtendedSearchRequestModel
	| GalContactExtendedSearchRequestModel;

export enum ContactExtendedSearchResultKind {
	LazyLoadPage = 1,
	SkipTokenPage = 2
}

export type DefaultContactExtendedSearchResultModel = {
	kind: ContactExtendedSearchResultKind.LazyLoadPage;
	lazyLoadPageResult: LazyLoadPageResult<ContactGlobalSearchResultItem>;
};

export type GalContactExtendedSearchResultModel = {
	kind: ContactExtendedSearchResultKind.SkipTokenPage;
	skipTokenPageResult: SkipTokenPageResult<ContactGlobalSearchResultItem>;
};

export type ContactExtendedSearchResultModel =
	| DefaultContactExtendedSearchResultModel
	| GalContactExtendedSearchResultModel;

export function searchContacts(
	request: DefaultContactExtendedSearchRequestModel,
	signal?: AbortSignal
): Promise<DefaultContactExtendedSearchResultModel>;
export function searchContacts(
	request: GalContactExtendedSearchRequestModel,
	signal?: AbortSignal
): Promise<GalContactExtendedSearchResultModel>;
export function searchContacts(
	request: ContactExtendedSearchRequestModel,
	signal?: AbortSignal
): Promise<ContactExtendedSearchResultModel> {
	return environmentHttpClient.get<ContactExtendedSearchResultModel>(
		"/TitleBar/Search/Contacts",
		request,
		{ signal }
	);
}

export type IntegrationFlowSearchRequestModel = {
	searchTerm: string;
	maxAmount: number;
};

export function searchIntegration(
	integrationId: number,
	request: IntegrationFlowSearchRequestModel,
	signal?: AbortSignal
): Promise<readonly IntegrationFlowGlobalSearchResultItem[]> {
	return environmentHttpClient.get<readonly IntegrationFlowGlobalSearchResultItem[]>(
		`/TitleBar/Search/Integration/${integrationId}`,
		request,
		{
			signal
		}
	);
}

export function validateAuthentications(
	integrationId: number,
	signal?: AbortSignal
): Promise<IntegrationFlowMultipleAuthenticationsAreRequiredModel> {
	return environmentHttpClient.get<IntegrationFlowMultipleAuthenticationsAreRequiredModel>(
		`TitleBar/Search/Integration/${integrationId}/AuthenticationsToComplete`,
		undefined,
		{ signal }
	);
}

export type CommunityPagingInfoModel = {
	name: string;
};

export type CommunitiesSearchRequestModel = {
	searchTerm: string;
	shouldBeMember: OptionalModel<boolean>;
	sortDirection: SortDirection;
	pagingInfo: OptionalModel<CommunityPagingInfoModel>;
	amount: number;
};

export type CommunityGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.Community;
	hitHighlights: readonly HighlightItem[];
	id: number;
	name: string;
	visibility: CommunityVisibility;
	isCurrentUserMember: boolean;
	memberCount: number;
	color: AppColorViewModel;
};

export function searchCommunities(
	model: CommunitiesSearchRequestModel,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<CommunityGlobalSearchResultItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<CommunityGlobalSearchResultItem>>(
		`TitleBar/Search/Communities`,
		model,
		{ signal }
	);
}

export type CommunityPostGlobalSearchResultItemStatisticsModel = {
	reactionCounters: readonly ReactionCounterModel[];
	commentsCount: number;
};

export type CommunityPostGlobalSearchResultItem = {
	kind: GlobalSearchResultItemKind.CommunityPost;
	hitHighlights: readonly HighlightItem[];
	id: number;
	createdBy: UserIdentityModel | null;
	createdOn: string;
	community: CommunityViewModel;
	messagePreview: string | null;
	statistics: CommunityPostGlobalSearchResultItemStatisticsModel;
	images: readonly CommunityPostImageViewModel[];
};

export type CommunityPostSearchPagingInfoModel = {
	id: number;
	createdOn: string;
};

export type CommunityPostSearchRequestModel = {
	searchTerm: string;
	communityIds?: readonly number[];
	authorIds?: readonly number[];
	sortDirection: SortDirection;
	amount: number;
	pagingInfo: OptionalModel<CommunityPostSearchPagingInfoModel>;
};

export function searchCommunityPosts(
	model: CommunityPostSearchRequestModel,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<CommunityPostGlobalSearchResultItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<CommunityPostGlobalSearchResultItem>>(
		`TitleBar/Search/CommunityPosts`,
		model,
		{ signal }
	);
}

export type BaseScopeSearchNodeModel = {
	kind: SearchNodeKind.Scope;
	usageSortOrder: number;
	name: string;
};

export type PredefinedScopeSearchNodeModel = BaseScopeSearchNodeModel & {
	scopeKind: GlobalSearchScopeKind.Predefined;
	predefinedKind: SearchIntegrationKind;
};

export type IntegrationFlowScopeSearchNodeModel = BaseScopeSearchNodeModel & {
	scopeKind: GlobalSearchScopeKind.IntegrationFlow;
	integrationId: number;
};

export type ScopeSearchNodeModel =
	| PredefinedScopeSearchNodeModel
	| IntegrationFlowScopeSearchNodeModel;

export type GroupSearchNodeModel = {
	kind: SearchNodeKind.Group;
	id: number;
	name: string;
	scopes: readonly ScopeSearchNodeModel[];
};

export type SearchNodeModel =
	| GroupSearchNodeModel
	| ScopeSearchNodeModel;

export function getSearchNodes(
	signal?: AbortSignal
): Promise<readonly SearchNodeModel[]> {
	return environmentHttpClient.get<readonly SearchNodeModel[]>(
		"TitleBar/Search/Nodes",
		undefined,
		{ signal }
	);
}
